import { useState } from 'react';
import { Copy, Check, Mail, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import type { CarePlanData } from '@/hooks/useCarePlan';

interface ShareSectionProps {
  plan: CarePlanData;
}

export function ShareSection({ plan }: ShareSectionProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const shareUrl = `${window.location.origin}/plan/${plan.id}?t=${plan.share_token}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSendEmail = async () => {
    if (!email) return;
    try {
      // Call edge function to send email
      const response = await fetch('/functions/v1/send-care-plan-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: plan.id,
          shareToken: plan.share_token,
          email,
          planName: plan.plan_name,
          savings: plan.savings_vs_national,
          optimizedTotal: plan.optimized_total_cost,
        }),
      });
      if (response.ok) {
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 3000);
      }
    } catch {
      // Silently fail — email is optional
    }
  };

  return (
    <div className={`rounded-xl border p-4 ${
      isAhp ? (ahpDark ? 'border-civic-gold/20 bg-civic-card-dark' : 'border-gray-200 bg-white') : 'border-foreground/10 bg-card'
    }`}>
      <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${
        isAhp ? (ahpDark ? 'text-civic-warm' : 'text-gray-900') : 'text-foreground'
      }`}>
        <Link2 className="w-4 h-4" />
        Share This Plan
      </h3>

      {/* Copy link */}
      <div className="flex gap-2 mb-3">
        <Input
          value={shareUrl}
          readOnly
          className={`text-xs ${isAhp ? (ahpDark ? 'bg-civic-bg-dark text-civic-warm' : 'bg-gray-50') : 'bg-background'}`}
          onClick={(e) => (e.target as HTMLInputElement).select()}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopy}
          className="shrink-0"
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>

      {/* Email */}
      <div className="flex gap-2">
        <Input
          type="email"
          placeholder="Send to email..."
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`text-xs ${isAhp ? (ahpDark ? 'bg-civic-bg-dark text-civic-warm' : 'bg-gray-50') : 'bg-background'}`}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={handleSendEmail}
          disabled={!email || emailSent}
          className="shrink-0"
        >
          {emailSent ? <Check className="w-4 h-4 text-green-500" /> : <Mail className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
